/**
 * 用户管理（管理员）。
 *
 * 用户列表（邮箱/角色/余额/状态）+ 封禁/解封 + 修改角色。
 *
 * @module pages/admin/UserAdminPage
 */
import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import type { SelectChangeEvent } from '@mui/material/Select';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import PageHeader from '@/components/common/PageHeader';
import DataTable, { type Column } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import MoneyText from '@/components/common/MoneyText';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { useToast } from '@/hooks/useToast';
import { useApi } from '@/hooks/useApi';
import { listUsers, patchUser } from '@/services/admin';
import type { User, UserRole } from '@/types';

/**
 * 用户管理页组件。
 *
 * @returns 页面元素。
 */
export default function UserAdminPage(): JSX.Element {
  const { data, loading, refetch } = useApi(() => listUsers());
  const { show, Toast } = useToast();

  const [banId, setBanId] = useState<number | null>(null);
  const [banAction, setBanAction] = useState<'ban' | 'unban'>('ban');
  const [busy, setBusy] = useState(false);

  const isBanned = (u: User): boolean => u.status !== 'active';

  const handleRoleChange = async (
    user: User,
    role: UserRole,
  ): Promise<void> => {
    try {
      await patchUser(user.id, { role });
      show('角色已更新');
      await refetch();
    } catch (err) {
      show(err instanceof Error ? err.message : '更新失败', 'error');
    }
  };

  const handleBanConfirm = async (): Promise<void> => {
    if (banId == null) return;
    setBusy(true);
    try {
      if (banAction === 'ban') {
        await patchUser(banId, { status: 'banned' });
        show('已封禁用户');
      } else {
        await patchUser(banId, { status: 'active' });
        show('已解封用户');
      }
      setBanId(null);
      await refetch();
    } catch (err) {
      show(err instanceof Error ? err.message : '操作失败', 'error');
    } finally {
      setBusy(false);
    }
  };

  const columns: Column<User>[] = [
    { key: 'email', header: '邮箱' },
    { key: 'name', header: '昵称', render: (u) => u.name || '-' },
    {
      key: 'role',
      header: '角色',
      render: (u) => (
        <Select
          size="small"
          value={u.role}
          onChange={(e: SelectChangeEvent) =>
            handleRoleChange(u, e.target.value as UserRole)
          }
          sx={{ minWidth: 96 }}
        >
          <MenuItem value="user">普通用户</MenuItem>
          <MenuItem value="admin">管理员</MenuItem>
        </Select>
      ),
    },
    {
      key: 'balance',
      header: '余额',
      align: 'right',
      render: (u) => <MoneyText value={u.balance} />,
    },
    {
      key: 'status',
      header: '状态',
      render: (u) =>
        isBanned(u) ? (
          <StatusChip label="已封禁" color="error" />
        ) : (
          <StatusChip label="正常" color="success" />
        ),
    },
    {
      key: 'actions',
      header: '操作',
      align: 'right',
      render: (u) => (
        <Button
          size="small"
          color={isBanned(u) ? 'success' : 'error'}
          startIcon={isBanned(u) ? <CheckCircleIcon /> : <BlockIcon />}
          onClick={() => {
            setBanAction(isBanned(u) ? 'unban' : 'ban');
            setBanId(u.id);
          }}
        >
          {isBanned(u) ? '解封' : '封禁'}
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader title="用户管理" subtitle="管理平台用户、角色与封禁状态" />

      <DataTable
        columns={columns}
        rows={data ?? []}
        rowKey={(u) => u.id}
        loading={loading}
        emptyText="暂无用户"
      />

      <ConfirmDialog
        open={banId != null}
        danger={banAction === 'ban'}
        title={banAction === 'ban' ? '封禁用户' : '解封用户'}
        message={
          banAction === 'ban'
            ? '封禁后该用户将无法登录与使用服务，确定封禁？'
            : '确定解封该用户？'
        }
        confirmText={banAction === 'ban' ? '封禁' : '解封'}
        loading={busy}
        onConfirm={handleBanConfirm}
        onCancel={() => !busy && setBanId(null)}
      />
      {Toast}
    </Box>
  );
}
